/**
 * @author Mr_FabiozZz[mr.fabiozzz@gmail.com]
 */
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import sass from "./nav.module.sass";
import { ReactComponent as TableToggle } from "images/TableTogglerLeft.svg";
import cn from "classnames";
import { tabsList as initialTabsList } from "./tabsList";
import { componentsList } from "pages/StepPages/StepFour/One/Tables/TableNavigate/componentsList";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { organizationAPI } from "libs/apiClient/Organization";

export interface ITab {
	id: number;
	tab: string;
	name: string;
	filled: string;
}

interface IDirection {
	direction: "right" | "left";
	exit: boolean;
}
enum EThreatTab {
	"SYSTEM" = "description",
	"RISK" = "types_of_risks",
	"INFLUENCE" = "objects_of_impact",
	"COLLUSION" = "collusion_groups",
	"ASSESSMENT" = "assessment_of_objectives",
	"LEVEL" = "levels_of_violators",
	"VIOLATOR" = "current_violators",
	"WAY" = "possible_objects",
	"TACTIC" = "list_of_basic",
	"ACTUAL" = "list_current_threats",
}

export const ChangeActiveTabContext = createContext<{
	changeTab: (tab: ITab, cur?: boolean) => void;
	updateTabs: (tab: keyof typeof EThreatTab) => void;
}>({} as { changeTab: (tab: ITab) => void; updateTabs: (tab: keyof typeof EThreatTab) => void });

const TableNavigate: React.FC<{ thread: number | undefined }> = ({ thread }) => {
	const [tabsList, setTabsList] = useState(initialTabsList);

	const navigate = useNavigate();
	const location = useLocation();

	const paramsURL = useParams();

	/**
	 *  стэйт для определения в какую сторону делать анимацию при переключении табов
	 */
	const [direction, setDirection] = useState<IDirection>({ direction: "right", exit: false });

	/**
	 *  активный таб
	 */
	const [active, setActive] = useState<ITab>({
		id: 1,
		name: "Описание системы и ее характеристика как объекта защиты",
		tab: "description",
		filled: "",
	});

	/**
	 *  это для формирования порций табов
	 */
	const [scrollTab, setScrollTab] = useState<"left" | "right">("left");

	/**
	 *  хранится информация для первой отрисовки компонента
	 */
	const oneLoad = useRef(false);
	const changedTab = useRef(true);

	useEffect(() => {
		if (!oneLoad.current && tabsList.length) {
			const copyPath = location.pathname;
			const startIndex = location.pathname.lastIndexOf("/");
			const keyTab = copyPath.slice(startIndex + 1);
			const tab = tabsList.find((_) => _.tab === keyTab);
			if (tab) {
				if (tab.id >= 6) {
					setScrollTab("right");
				} else {
					changedTab.current = false;
				}
				changeActiveTab(tab);
			}
		}
		//eslint-disable-next-line
	}, []);

	/**
	 * Принимает кликнутый таб, проверяет справа или слева от активного до этого таба кликнули
	 * в зависимости от этого включает анимацию отключения активной вкладки
	 * затем анимирует вход нового
	 * @param {ITab} tab - сам таб на который кликает пользователь
	 * @param current
	 */
	const changeActiveTab = (tab: Omit<ITab, "filled">, current?: boolean) => {
		if (tab.id !== active.id) {
			if (tab.id > active.id) {
				setDirection({ direction: "left", exit: true });
			} else {
				setDirection({ direction: "right", exit: true });
			}
			if (current !== undefined) {
				oneLoad.current = false;
				setScrollTab(tab.id <= 5 ? "left" : "right");
			}
			setTimeout(() => {
				setDirection((prevState) => ({ ...prevState, exit: false }));
				const find = tabsList.find((_) => _.id === tab.id);
				if (find) {
					setActive(find);
					navigate(location.pathname.replace(/\/(.+)$/, find.tab), { replace: true });
				}
			}, 100);
		}
	};

	/**
	 * мемоизированная разметка, меняется при изменении активной вкладки
	 * и при необходимости для построения использует другую часть массива табов
	 */
	const renderList = useMemo(() => {
		let any;
		if (scrollTab === "left") {
			any = [...tabsList].splice(0, 5) as ITab[];
		} else {
			any = [...tabsList].splice(5, tabsList.length) as ITab[];
		}
		return any.map((_, index) => {
			return (
				<div
					key={index}
					data-tab={_.tab}
					className={cn(sass.list_item, _.id === active.id && sass["list_item-active"], {
						[sass.empty]: _.filled === "EMPTY",
						[sass.partial]: _.filled === "PARTIAL",
					})}
					onClick={() => changeActiveTab(_)}>
					<p>{_.name}</p>
				</div>
			);
		});
		//eslint-disable-next-line
	}, [active, scrollTab, tabsList]);

	/**
	 * при первой загрузке ничего не делает, далее следит за кликом для перестроения вкладок
	 * если выбрана левая часть табов, то активной вкладкой становится последняя из 5 и наоборот
	 */
	useEffect(() => {
		if (oneLoad.current) {
			if (scrollTab === "left") {
				if (!changedTab.current) {
					const tab = tabsList.find((_) => _.tab === "assessment_of_objectives");
					changeActiveTab(tab!);
				}
			} else {
				if (!changedTab.current) {
					const tab = tabsList.find((_) => _.tab === "levels_of_violators");
					changeActiveTab(tab!);
				}
			}
			changedTab.current = false;
		} else {
			oneLoad.current = true;
		}
		//eslint-disable-next-line
	}, [scrollTab]);

	/**
	 * при переключении таба берется необходимый компонент и возвращает его
	 */
	const Component = useMemo(() => {
		return componentsList[active.tab as keyof typeof componentsList];
	}, [active]);

	const updateTabs = useCallback(
		(
			tabKey?:
				| "SYSTEM"
				| "RISK"
				| "INFLUENCE"
				| "COLLUSION"
				| "ASSESSMENT"
				| "LEVEL"
				| "VIOLATOR"
				| "WAY"
				| "TACTIC"
				| "ACTUAL",
		) => {
			const query = { tab: tabKey };
			// @ts-ignore
			organizationAPI.objectStatusFillDetail(Number(paramsURL.threadId, query)).then(({ data }) => {
				const one = data.find((_) => _.tab === tabKey);
				setTabsList((prevState) =>
					[...prevState].map((_) => (_.tab === EThreatTab[one!.tab] ? { ..._, filled: one!.status } : _)),
				);
			});
		},
		[paramsURL.threadId],
	);

	useEffect(() => {
		if (thread) {
			organizationAPI.objectStatusFillDetail(Number(paramsURL.threadId)).then(({ data }) => {
				const filedData = data.map((_) => ({ tab: EThreatTab[_.tab], filled: _.status }));
				setTabsList((prevState) => {
					return [...prevState].map((_) => {
						const find = filedData.find((item) => item.tab === _.tab);
						return { ..._, ...find! };
					});
				});
			});
		}
		//eslint-disable-next-line
	}, [thread]);
	return (
		<>
			<div data-comment-false={true} className={sass.wrapper}>
				<div
					onClick={() => scrollTab === "right" && setScrollTab("left")}
					className={cn(sass.wrapper_arrow, scrollTab === "right" && sass["wrapper_arrow-active"])}>
					<TableToggle />
				</div>
				{renderList}
				<div
					onClick={() => scrollTab === "left" && setScrollTab("right")}
					className={cn(sass.wrapper_arrow, scrollTab === "left" && sass["wrapper_arrow-active"])}>
					<TableToggle />
				</div>
			</div>
			<div className={sass[`${direction.direction}_${direction.exit ? "out" : "enter"}`]}>
				<ChangeActiveTabContext.Provider value={{ changeTab: changeActiveTab, updateTabs }}>
					<Component />
				</ChangeActiveTabContext.Provider>
			</div>
		</>
	);
};

export default TableNavigate;
